import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import { FireCalculatorService, FireResult, FIRE_CONSTRAINTS } from './fire-calculator.service';

@Component({
  selector: 'app-fire-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './fire-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fire-calculator.scss']
})
export class FireCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(FireCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = FIRE_CONSTRAINTS;
  readonly Math = Math;

  currentAge = 30;
  retirementAge = 50;
  lifeExpectancy = 85;
  currentMonthlyExpense = 50_000;
  currentSavings = 1_000_000;
  monthlySip = 30_000;
  preReturnRate = 12;
  postReturnRate = 8;
  inflationRate = 6;
  swr = 4;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     FireResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};

    const e1 = validateRange(this.currentAge, this.C.currentAge.min, this.C.currentAge.max, 'Current age');
    if (e1) errors['currentAge'] = e1;

    const e2 = validateRange(this.retirementAge, this.C.retirementAge.min, this.C.retirementAge.max, 'Retirement age');
    if (e2) errors['retirementAge'] = e2;
    else if (this.retirementAge <= this.currentAge) errors['retirementAge'] = 'Retirement age must be greater than current age.';

    const e3 = validateRange(this.lifeExpectancy, this.C.lifeExpectancy.min, this.C.lifeExpectancy.max, 'Life expectancy');
    if (e3) errors['lifeExpectancy'] = e3;
    else if (this.lifeExpectancy <= this.retirementAge) errors['lifeExpectancy'] = 'Life expectancy must be greater than retirement age.';

    const e4 = validateRange(this.currentMonthlyExpense, this.C.monthlyExpenses.min, this.C.monthlyExpenses.max, 'Monthly expenses');
    if (e4) errors['currentMonthlyExpense'] = e4;

    const e5 = validateRangeAllowZero(this.currentSavings, this.C.currentSavings.min, this.C.currentSavings.max, 'Current savings');
    if (e5) errors['currentSavings'] = e5;

    const e6 = validateRangeAllowZero(this.monthlySip, this.C.monthlySip.min, this.C.monthlySip.max, 'Monthly SIP investment');
    if (e6) errors['monthlySip'] = e6;

    const e7 = validateRange(this.preReturnRate, this.C.preReturn.min, this.C.preReturn.max, 'Pre-retirement return');
    if (e7) errors['preReturnRate'] = e7;

    const e8 = validateRange(this.postReturnRate, this.C.postReturn.min, this.C.postReturn.max, 'Post-retirement return');
    if (e8) errors['postReturnRate'] = e8;

    const e9 = validateRangeAllowZero(this.inflationRate, this.C.inflation.min, this.C.inflation.max, 'Inflation rate');
    if (e9) errors['inflationRate'] = e9;

    const e10 = validateRange(this.swr, this.C.swr.min, this.C.swr.max, 'Safe withdrawal rate');
    if (e10) errors['swr'] = e10;

    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'currentAge')      this.currentAge      = Math.floor(this.currentAge || 0);
    if (field === 'retirementAge')   this.retirementAge   = Math.floor(this.retirementAge || 0);
    if (field === 'lifeExpectancy')  this.lifeExpectancy  = Math.floor(this.lifeExpectancy || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      currentAge: true, retirementAge: true, lifeExpectancy: true,
      currentMonthlyExpense: true, currentSavings: true, monthlySip: true,
      preReturnRate: true, postReturnRate: true, inflationRate: true, swr: true
    };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate({
      currentAge:            Math.floor(this.currentAge),
      retirementAge:         Math.floor(this.retirementAge),
      lifeExpectancy:        Math.floor(this.lifeExpectancy),
      currentMonthlyExpense: this.currentMonthlyExpense,
      currentSavings:        this.currentSavings,
      monthlySip:            this.monthlySip,
      preReturnRate:         this.preReturnRate,
      postReturnRate:        this.postReturnRate,
      inflationRate:         this.inflationRate,
      swr:                   this.swr
    });
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Invested', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Wealth Gained', value: Math.max(r.totalWealthGain, 0), color: '#10b981' }
      ], 'Corpus at Retirement');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Age ${row.age}`),
        [
          { label: 'FI Target Corpus', data: r.years.map(row => Math.round(row.requiredCorpus)), color: '#f59e0b', dashed: true },
          { label: 'Your Corpus', data: r.years.map(row => Math.round(row.investedCorpus)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.currentAge            = 30;
    this.retirementAge         = 50;
    this.lifeExpectancy        = 85;
    this.currentMonthlyExpense = 50_000;
    this.currentSavings        = 1_000_000;
    this.monthlySip            = 30_000;
    this.preReturnRate         = 12;
    this.postReturnRate        = 8;
    this.inflationRate         = 6;
    this.swr                   = 4;
    this.formErrors            = {};
    this.touched               = {};
    this.showFormErrors        = false;
    this.calculated            = false;
    this.result                = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
